import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ExternalLink, FileText, RotateCw, X } from "lucide-react";
import {
  nextDiagramRotation,
  readDiagramRotation,
  writeDiagramRotation,
} from "../diagramRotation";
import type { DiagramRotation } from "../diagramRotation";
import type { DiagramPageImageStatus, DiagramPageModalProps } from "./types";

export const DiagramPageModal = ({
  open,
  onClose,
  designator,
  label,
  page,
  imageUrl,
  documentKey,
  onOpenFullView,
}: DiagramPageModalProps) => {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<DiagramPageImageStatus>("loading");
  const [rotation, setRotation] = useState<DiagramRotation>(0);
  // The frame the page has to fit inside. A quarter-turned image is laid out at
  // its UNROTATED size, so `object-contain` would fit the wrong box and the
  // sheet would hang out of the modal — it has to be constrained by the frame's
  // opposite side instead, and that needs the frame measured.
  const [frame, setFrame] = useState<{ width: number; height: number } | null>(
    null,
  );

  const showsImage = Boolean(imageUrl) && status !== "failed";
  const printedAs = label && label !== designator ? designator : null;
  const title = t("smartSurveyor.cablesBeta.diagramModalTitle", {
    designator: label || designator,
  });
  // "Drawn as -2F1 on page 4" earns its place only when the schematic prints a
  // DIFFERENT name from the one he uses — that is the join he cannot make in his
  // head. When they are the same name it was repeating the title, so the line
  // says only where it is.
  const pageLine =
    page === null
      ? null
      : printedAs
        ? t("smartSurveyor.cablesBeta.diagramModalPage", {
            designator: printedAs,
            page,
          })
        : t("smartSurveyor.cablesBeta.diagramModalPageOnly", { page });
  const imageAlt = pageLine ? `${title} — ${pageLine}` : title;
  const isQuarterTurn = rotation === 90 || rotation === 270;

  const rotate = useCallback(() => {
    setRotation((current) => {
      const next = nextDiagramRotation(current);
      writeDiagramRotation({ documentKey, rotation: next });
      return next;
    });
  }, [documentKey]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // A page he already looked at is fetched again from scratch when he reopens it.
  useEffect(() => {
    setStatus("loading");
  }, [imageUrl, open]);

  // The orientation he last set for this schematic, read on every opening —
  // "rotated" is a property of the document, not of this viewing of it.
  useEffect(() => {
    if (!open) return;
    setRotation(readDiagramRotation({ documentKey }));
  }, [open, documentKey]);

  useEffect(() => {
    const node = frameRef.current;
    if (!open || !node) return;
    const measure = () =>
      setFrame({ width: node.clientWidth, height: node.clientHeight });
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, showsImage]);

  if (!open) return null;

  return (
    <div
      data-testid="diagram-page-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onClose}
    >
      <div
        data-testid="diagram-page-modal"
        className={clsx(
          "flex w-full flex-col overflow-hidden rounded-xl bg-white shadow-xl",
          // A drawing wants the whole screen; a line of text in a screen-sized
          // box is the empty white box this modal exists to avoid.
          showsImage ? "h-[92vh] max-w-[92vw]" : "max-h-[80vh] max-w-sm",
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-700">
            <FileText size={15} className="flex-none text-indigo-500" />
            <span className="truncate">{title}</span>
            {showsImage && pageLine && (
              <span className="truncate text-[12.5px] font-normal text-slate-400">
                {pageLine}
              </span>
            )}
          </div>
          <div className="ml-3 flex flex-none items-center gap-1">
            {showsImage && (
              <button
                type="button"
                data-testid="diagram-page-rotate"
                onClick={rotate}
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                title={t("smartSurveyor.cablesBeta.diagramModalRotate")}
                aria-label={t("smartSurveyor.cablesBeta.diagramModalRotate")}
              >
                <RotateCw size={16} />
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              title={t("smartSurveyor.cablesBeta.diagramModalClose")}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-slate-50 px-5 py-4">
          {showsImage && imageUrl ? (
            <div
              ref={frameRef}
              className="relative flex h-full min-h-0 items-center justify-center overflow-hidden"
            >
              {status === "loading" && (
                <div
                  data-testid="diagram-page-loading"
                  className="absolute inset-0 flex items-center justify-center text-[13px] text-slate-400"
                >
                  {t("smartSurveyor.cablesBeta.diagramModalLoading")}
                </div>
              )}
              <img
                data-testid="diagram-page-image"
                data-rotation={rotation}
                src={imageUrl}
                alt={imageAlt}
                onLoad={() => setStatus("shown")}
                onError={() => setStatus("failed")}
                className={clsx(
                  "object-contain",
                  !isQuarterTurn && "max-h-full max-w-full",
                  status === "loading" && "opacity-0",
                )}
                style={{
                  transform: rotation ? `rotate(${rotation}deg)` : undefined,
                  // Sideways, the page is still LAID OUT upright, so its limits
                  // are the frame's opposite sides.
                  ...(isQuarterTurn && frame
                    ? {
                        maxWidth: `${frame.height}px`,
                        maxHeight: `${frame.width}px`,
                      }
                    : {}),
                }}
              />
            </div>
          ) : (
            <div className="space-y-2">
              {pageLine && (
                <p className="text-sm leading-relaxed text-slate-700">
                  {pageLine}
                </p>
              )}
              <p className="text-[12.5px] leading-relaxed text-slate-500">
                {t("smartSurveyor.cablesBeta.diagramModalNoImage")}
              </p>
            </div>
          )}
        </div>

        {onOpenFullView && (
          <div className="flex-none border-t border-slate-200 px-4 py-2">
            <button
              type="button"
              onClick={onOpenFullView}
              className="-ml-1.5 flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[12.5px] text-slate-500 hover:text-slate-800"
            >
              <ExternalLink size={13} className="flex-none text-slate-400" />
              {t("smartSurveyor.cablesBeta.diagramModalOpenFull")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
